'use client';
import React, { useState } from 'react';
import { ThumbsDown, Bookmark, Copy, Check, Download } from 'lucide-react';

interface AnswerActionsProps {
  question: string;
  answer: string;
  citations?: Array<{ sourceLabel: string; url: string; text: string }>;
  sessionId: string;
  messageId: string;
}

export default function AnswerActions({
  question,
  answer,
  citations = [],
  sessionId,
  messageId,
}: AnswerActionsProps) {
  const [flagged, setFlagged] = useState(false);
  const [flagLoading, setFlagLoading] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [saved, setSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleFlag = async () => {
    if (flagged || flagLoading) return;
    setFlagLoading(true);
    try {
      await fetch('/api/flag-answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, answer, reason: flagReason, session_id: sessionId }),
      });
      setFlagged(true);
      setShowFlagModal(false);
      setFlagReason('');
    } catch {
      (window as any).__debugLog?.('AnswerActions', 'Flag failed', 'error');
    } finally {
      setFlagLoading(false);
    }
  };

  const handleSave = async () => {
    if (saved || saveLoading) return;
    setSaveLoading(true);
    try {
      await fetch('/api/save-answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, answer, citations, session_id: sessionId }),
      });
      setSaved(true);
    } catch {
      (window as any).__debugLog?.('AnswerActions', 'Save failed', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCopyFormatted = async () => {
    const citationBlock =
      citations.length > 0
        ? '\n\nΝΟΜΙΚΕΣ ΑΝΑΦΟΡΕΣ:\n' +
          citations.map((c) => `• ${c.sourceLabel}: ${c.url}`).join('\n')
        : '';
    const disclaimer =
      '\n\n──────────────────────────\nΠαρήχθη από AskΦόρος · Πληροφοριακό υλικό — δεν αποτελεί φορολογική ή νομική συμβουλή.\n──────────────────────────';
    const text = `ΕΡΩΤΗΣΗ: ${question}\n\nΑΠΑΝΤΗΣΗ:\n${answer}${citationBlock}${disclaimer}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPDF = () => {
    const citationBlock =
      citations.length > 0
        ? '\n\nΝΟΜΙΚΕΣ ΑΝΑΦΟΡΕΣ:\n' +
          citations.map((c) => `• ${c.sourceLabel}: ${c.url}`).join('\n')
        : '';
    const disclaimer =
      '\n\n──────────────────────────\nΠαρήχθη από AskΦόρος · Πληροφοριακό υλικό — δεν αποτελεί φορολογική ή νομική συμβουλή.';
    const content = `ΕΡΩΤΗΣΗ: ${question}\n\nΑΠΑΝΤΗΣΗ:\n${answer}${citationBlock}${disclaimer}`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logistis-ai-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
        {/* Copy formatted */}
        <button
          onClick={handleCopyFormatted}
          title="Αντιγραφή με παραπομπές"
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          {copied ? <Check size={11} className="text-green-600" /> : <Copy size={11} />}
          <span className="hidden sm:inline">{copied ? 'Αντιγράφηκε' : 'Αντιγραφή'}</span>
        </button>

        {/* Export */}
        <button
          onClick={handleExportPDF}
          title="Εξαγωγή ως αρχείο"
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <Download size={11} />
          <span className="hidden sm:inline">Εξαγωγή</span>
        </button>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saved || saveLoading}
          title={saved ? 'Αποθηκεύτηκε' : 'Αποθήκευση'}
          className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
            saved
              ? 'text-blue-600 cursor-default' :'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          <Bookmark size={11} className={saved ? 'fill-blue-600' : ''} />
          <span className="hidden sm:inline">{saved ? 'Αποθηκεύτηκε' : 'Αποθήκευση'}</span>
        </button>

        {/* Flag */}
        <button
          onClick={() => !flagged && setShowFlagModal(true)}
          disabled={flagged}
          title={flagged ? 'Αναφέρθηκε' : 'Αναφορά λάθους'}
          className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
            flagged
              ? 'text-red-400 cursor-default' :'text-gray-400 hover:text-red-500 hover:bg-red-50'
          }`}
        >
          <ThumbsDown size={11} />
          <span className="hidden sm:inline">{flagged ? 'Αναφέρθηκε' : 'Λάθος;'}</span>
        </button>
      </div>

      {/* Legal disclaimer — always visible under every answer */}
      <p className="mt-2 text-xs text-gray-400 border-t border-gray-100 pt-1.5">
        Πληροφοριακό υλικό βάσει δημοσίως διαθέσιμης νομοθεσίας — δεν αποτελεί φορολογική ή
        νομική συμβουλή. Συμβουλευτε αδειοδοτημένο επαγγελματία.
      </p>

      {/* Flag modal */}
      {showFlagModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowFlagModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-5 animate-slide-up">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Αναφορά λάθους</h3>
            <p className="text-xs text-gray-500 mb-3">
              Βοηθάτε στη βελτίωση του AskΦόρος. Περιγράψτε προαιρετικά το πρόβλημα.
            </p>
            <textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="π.χ. Λάθος ποσοστό ΦΠΑ, ξεπερασμένη πληροφορία..."
              rows={3}
              className="input-field mb-3 resize-none"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowFlagModal(false)}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                Ακύρωση
              </button>
              <button
                onClick={handleFlag}
                disabled={flagLoading}
                className="btn-danger text-xs py-1.5 px-3 flex items-center gap-1.5"
              >
                {flagLoading ? 'Αποστολή...' : 'Αποστολή αναφοράς'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
